/**
 * @module lib/commercial/dfp-ads
 * @summary Double Click for Publishers Ads
 * @description Manage the process all of the static Ad markup that was server-side rendered on the page.
 * Maybe filter some of the inline ads out, and determine ad slot sizes based on page and ad position context.
 * @see https://developers.google.com/doubleclick-gpt/reference - GPT Reference
 * @see https://support.google.com/dfp_premium/answer/3423562 - Responsive ads
 * @see https://support.google.com/dfp_premium/answer/4578089#lazyloading - Lazy loading
 * @see https://developers.google.com/doubleclick-gpt/common_implementation_mistakes - Avoiding mistakes
 */
import tgamConfig from "config/tgam";
import difference from "lodash/difference";
import throttle from "lodash/throttle";

import { isMobile, isTablet, isDesktop } from "utils/detect";
import logger from "utils/logger";
import { qsa, getProp } from "utils/utils";

import { setupPageTargets, slotUnitPath } from "lib/commercial/adops/page-targets";
import ee from "lib/eventemitter";
import { mark, measure } from "lib/performance/user-timing";

import { filterPageAdverts } from "./adslot-size-mapping";
import DynamicAd from "./dynamic-ad";
import { gptAttachScript } from "./gpt-ads";
import initAdServer, { displayAdSlots } from "./header-bidding";
import { setupAdvertSticky, refreshStickyAll } from "./sticky-ads";
import { filterTierAdverts, reApplyAdIds } from "./tier-ads";
import dynamicAdUserTracking from "./user-events";

const debugTag = "[DFP-ADS]";

/**
 * @method initDfpAds
 * @description Gather all the GPT ad slots on the page, and process them for submission.
 */
export default function initDfpAds() {
  mark("dfp_ads_init_started");
  logger.groupCollapsed(`${debugTag} init`);

  // Page targets info is set onto each GPT ad slot, to help improve
  // it's contextual relevance for the given page and user.
  // Initialize these values, even if there are no ads on the page.
  setupPageTargets();

  let allPageUnits = qsa(".js-c-gpt-ad");
  if (allPageUnits.length) {
    gptAttachScript();

    // filter out the ads that don't belong within the current viewport tier.
    const tierPageUnits = filterTierAdverts(allPageUnits);

    // filter out the ads that don't have size mappings for the current tier
    const filteredUnits = filterPageAdverts(tierPageUnits, tgamConfig.meta);

    // destroy/remove the ads in the DOM that were filtered out
    const destroyUnits = difference(tierPageUnits, filteredUnits);
    destroyUnits.forEach(destroyAdvert);

    // re-apply ids in sequential order by type, to remaining ad elements
    reApplyAdIds(filteredUnits);

    const dynamicAds = processPageUnits(filteredUnits);
    const deferredAds = dynamicAds.filter(dynamicAd => dynamicAd.deferImmediate);

    logger.info(`${debugTag} page units removed (wrong tier):`, difference(allPageUnits, tierPageUnits).map(u => u.id));
    logger.info(`${debugTag} page units removed (no sizes for curent tier):`, destroyUnits.map(u => u.id));
    logger.info(`${debugTag} page units remaining:`, dynamicAds.map(ad => ad.id));

    // Initialize the ad server, header bidding, etc.
    initAdServer();

    // Batch all of the immediately-loading, deferred ads into a single Prebid
    // request. Non-deferred ads will display themselves automatically.
    const promises = deferredAds.map(dynamicAd => dynamicAd.deferImmediateReady);
    Promise.all(promises).then(() => {
      displayAdSlots(deferredAds);
    });

    // Sticky positioned ads appear on the premium section pages.
    // Not to be confused with an article's right-rail sticky-stack ads.
    const stickyUnits = setupAdvertSticky();
    if (stickyUnits.length) {
      logger.info(`${debugTag} page units (sticky):`, stickyUnits);
    }
  }
  logger.groupEnd(`${debugTag} init`);

  mark("dfp_ads_init_done");
  measure("dfp_ads_init");
}

/**
 * @method destroyAdvert
 * @description Remove and Ad and some of it's wrapper elements too.
 * @param {HTMLElement} el - of advert to remove from the DOM
 */
export function destroyAdvert(el) {
  if (el) {
    const adParent = el.closest(".c-ad");
    if (adParent) {
      // try to remove relative parent ad element (grey box) which has padding/margin
      adParent.parentNode.removeChild(adParent);
    } else {
      // otherwise just remove immediate parent
      el.parentNode.removeChild(el);
    }
  }
}

/**
 * @method processPageUnits
 * @description Process all "static", in-page ad units.
 * @param {Array} pgUnits - of ad slot units
 * @returns {Array} of objects representing an ad
 */
function processPageUnits(pgUnits) {
  mark("dfp_ads_process_started");

  // Determine if certain ads should auto-fresh for the current page content and viewport tier.
  const isAdv = getProp(tgamConfig, "meta.advContent", false);
  const isStory = getProp(tgamConfig, "meta.isStoryPage", false);
  const isSection = getProp(tgamConfig, "meta.isSectionPage", false);
  const shouldRefresh = (isStory || isSection) &&
    !isAdv &&
    (isDesktop() || isTablet("tabletWide"));

  // Ad-refreshing be applied to these ad types on all non-ADV article pages, for non-mobile viewport tiers.
  const refresh = shouldRefresh ? getProp(tgamConfig, "env.adRefreshCount") : 0;
  const refreshable = ["ldbd", "boxr", "boxs", "halfpager"]; // whitelist

  // use the same unit path for all ads
  const adUnitPath = slotUnitPath();

  dynamicAdUserTracking();

  // create an ad (slot) for each in-page unit
  const dynamicAds = pgUnits.map(unit => {
    const dad = Object.create(DynamicAd);
    const isCloned = unit.classList.contains("js-f-ad-cloned");
    const isLazyLoaded = unit.classList.contains("js-c-gpt-ad--lazy");
    const isHtmlAd = unit.id.includes("html-gpt");

    // mobile ads are always lazy-loaded, except for 1x1 HTML ads, which are always immediate-loaded.
    // non-mobile ads are immediate-loaded, unless they've been cloned
    const immediateLoad = (isMobile() && !isHtmlAd || isLazyLoaded) ? false : !isCloned;

    dad.init({
      id: unit.id,
      cloned: isCloned,
      immediate: immediateLoad,
      deferImmediate: immediateLoad,
      refresh: refreshable.indexOf(unit.dataset.type) > -1 ? refresh : 0,
      interval: getProp(tgamConfig, "env.adRefreshInterval"), // seconds
      unitpath: adUnitPath,
      onRendered: () => {
        // premium section pages have sticky right-rail boxr ads
        // standard article pages have sticky right-rail boxr and halfpager ads (plus sticky-stack)
        if (["boxr", "boxs", "halfpager"].indexOf(unit.dataset.type) > -1) {
          refreshStickyAll();
        }
        adInteractions(unit);
      }
    });
    return dad;
  });

  mark("dfp_ads_process_done");
  measure("dfp_ads_process");

  return dynamicAds;
}

/**
 * @method adInteractions
 * @description Triggers events when the user hovers over or out of an ad slot
 * @param {Object} unit - ad slot page unit
 */
function adInteractions(unit) {
  unit.addEventListener("mouseover", throttle(() => {
    ee.trigger("ad:over");
  }, 1000));
  unit.addEventListener("mouseout", throttle(() => {
    ee.trigger("ad:out");
  }, 1000));
}
